import { formatAccruedAmount } from "@common/helpers/domains/accounting/accruedBillingRule";
import { SCHEDULE_BILLING_INELIGIBLE_MESSAGE } from "@common/helpers/domains/accounting/scheduleBillingEligibility";
import {
    fromMonetaryToInt,
    subtractMonetaryValues,
} from "@common/helpers/monetary";
import { SCHEDULE_MARK_BILLED_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleBilling.schema";
import { pluralizeWithCount } from "@common/helpers/string";
import { ScheduleType } from "@common/types/domains/accounting/schedule";

interface AccruedSideCopy {
    side: string;
    billingDocument: string;
}

function getAccruedSideCopy(scheduleType: ScheduleType): AccruedSideCopy {
    return scheduleType === ScheduleType.revenue
        ? { side: "revenue", billingDocument: "invoice" }
        : { side: "expense", billingDocument: "vendor bill" };
}

export const SCHEDULE_MARK_BILLED_LABEL = "Mark as billed";
export const SCHEDULE_MARK_UNBILLED_LABEL = "Mark as unbilled";
export const SCHEDULE_MARKED_UNBILLED_TOAST = "Marked as unbilled";
export const SCHEDULE_BULK_BILLING_DISABLED_REASON =
    SCHEDULE_BILLING_INELIGIBLE_MESSAGE;
export const SCHEDULE_BULK_BILLING_FULLY_BILLED_REASON =
    "Deselect schedules that are already billed in full.";
export const SCHEDULE_BULK_BILLING_NOTHING_TO_BILL_REASON =
    "Deselect schedules with nothing to bill yet.";
export const SCHEDULE_BULK_BILLING_ONE_ENTITY_REASON =
    "Select schedules from one entity to mark them as billed.";
export const SCHEDULE_BULK_BILLING_LIMIT_REASON = `Select up to ${SCHEDULE_MARK_BILLED_BULK_LIMIT} schedules to mark them as billed.`;

export function buildMarkUnbilledCopy(scheduleType: ScheduleType): {
    title: string;
    body: string;
    confirmLabel: string;
} {
    const { side } = getAccruedSideCopy(scheduleType);
    return {
        title: "Mark as unbilled?",
        body: `The billing entries for this schedule will be removed, and the accrued ${side} balance comes back.`,
        confirmLabel: SCHEDULE_MARK_UNBILLED_LABEL,
    };
}

export function buildRecognitionAccountHelperText(options: {
    recognitionAccountName: string;
    scheduleType: ScheduleType;
}): string {
    const { billingDocument } = getAccruedSideCopy(options.scheduleType);
    return `Make sure the ${billingDocument} or payment is categorized to ${options.recognitionAccountName}.`;
}

export function buildMarkBilledSuccessToast(options: {
    amount: number;
    unbilledBalance: number;
    scheduleType: ScheduleType;
}): string {
    if (
        fromMonetaryToInt(options.amount) >=
        fromMonetaryToInt(options.unbilledBalance)
    ) {
        return "Marked as billed";
    }
    const stillUnbilled = subtractMonetaryValues(
        options.unbilledBalance,
        options.amount,
    );
    return `Amount is smaller than the accrued balance. ${formatAccruedAmount(options.amount)} cleared from accrued ${getAccruedSideCopy(options.scheduleType).side}. ${formatAccruedAmount(stillUnbilled)} is still unbilled.`;
}

export function buildMarkBilledBulkSuccessToast(count: number): string {
    return `Marked ${pluralizeWithCount("schedule", count)} as billed`;
}

/** The first refusal, plus how many were refused when it isn't the only one. */
export function buildMarkBilledBulkFailureToast(
    failures: Array<{ message: string }>,
): string {
    const [first] = failures;
    if (!first) {
        return "Failed to mark schedules as billed";
    }
    if (failures.length === 1) {
        return first.message;
    }
    return `${first.message} ${pluralizeWithCount("schedule", failures.length)} can't be marked as billed.`;
}
